import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEa } from "@/components/energy-analytics/analytics/EaContext";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaField, EaNote, EaPageHeader, EaSeg, inputCls } from "@/components/energy-analytics/analytics/EaUi";
import { EaExportButton, EaPointPicker, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { MONTHS_FULL, fmtQty } from "@/components/energy-analytics/analytics/eaFormat";
import { PageState } from "./EaPowerOverview";
import { EaCurveExport } from "@/components/energy-analytics/analytics/EaCurveExport";

const METRICS = { kwh: ["Consumo", "kWh", 0], kw: ["Potenza max", "kW", 0], cost: ["Costo teorico", "€", 2], pun: ["PUN", "€/MWh", 2] };
const HOURS = Array.from({ length: 24 }, (_, h) => String(h));

const OVER = "#8B32FB";

function color(v, min, max) {
  if (v == null) return "#F1F5F9";
  const t = max > min ? (v - min) / (max - min) : 0.5;
  return `hsl(${212 + t * 6}, ${80 + t * 18}%, ${95 - t * 62}%)`;
}

function Grid({ d, threshold, onCell }) {
  const [, u, dig] = METRICS[d.metric];
  return (
    <div className="overflow-x-auto" data-testid="ea-heatmap-grid">
      <table className="text-[10px] border-separate w-full min-w-[760px]" style={{ borderSpacing: 2 }}>
        <thead>
          <tr><th className="w-12" />{HOURS.map((h) => <th key={h} className="w-8 font-semibold text-slate-500">{h.padStart(2, "0")}</th>)}</tr>
        </thead>
        <tbody>
          {d.rows.map((r) => (
            <tr key={r.key} data-testid={`ea-heatmap-row-${r.key}`}>
              <td className="pr-2 text-right font-semibold text-slate-600 whitespace-nowrap">{d.mode === "days" ? `${r.weekday} ${r.label}` : r.label}{r.incomplete ? "*" : ""}</td>
              {HOURS.map((h) => {
                const v = r.cells[h];
                const mean = d.hour_mean[h];
                const over = threshold !== "" && v != null && v > Number(threshold);
                const dev = v != null && mean ? (v / mean - 1) * 100 : null;
                return (
                  <td key={h} onClick={() => d.mode === "days" && v != null && onCell(r.key)} data-testid={`ea-heatmap-cell-${r.key}-${h}`}
                    title={v == null ? "Nessun dato" : `${d.mode === "days" ? r.key.split("-").reverse().join("/") : r.label} ore ${h.padStart(2, "0")}:00 · ${fmtQty(v, dig, u)} · ${dev != null ? `${dev >= 0 ? "+" : ""}${fmtQty(dev, 0)}% vs media dell'ora` : ""}`}
                    className={`h-6 rounded-[3px] ${d.mode === "days" && v != null ? "cursor-pointer hover:ring-2 hover:ring-slate-900" : ""} `}
                    style={{ background: over ? OVER : color(v, d.min, d.max) }} />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
        <span>{fmtQty(d.min, dig, u)}</span>
        <div className="h-2 w-40 rounded" style={{ background: `linear-gradient(90deg, ${color(d.min, d.min, d.max)}, ${color(d.max, d.min, d.max)})` }} />
        <span>{fmtQty(d.max, dig, u)}</span>
        {threshold !== "" && <span className="ml-3 inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: OVER }} data-testid="ea-heatmap-over-legend" /> sopra soglia {fmtQty(Number(threshold), dig, u)}</span>}
      </div>
    </div>
  );
}

export default function EaPowerHeatmap() {
  const { filters, setFilter, search, nav } = useEa();
  const lastMonth = String(Number((nav?.availability?.last_curve_date || "2000-01").slice(5, 7)));
  const navigate = useNavigate();
  const codes = useSelectedCodes("POWER");
  const [mode, setMode] = useState("days");
  const [metric, setMetric] = useState("kwh");
  const [threshold, setThreshold] = useState("");
  const month = filters.month || (mode === "days" ? lastMonth : "");
  const { data: d, loading, error } = useEaData("/ea/power/heatmap", { codes, month, metric, mode });
  const header = <EaPageHeader title="Heatmap" subtitle="Giorni × ore oppure giorno della settimana × ora (valori medi). Clic su una cella per aprire il profilo a 15 minuti." right={<><EaCurveExport year={filters.year} /><EaPointPicker commodity="POWER" /></>} />;
  const [mLabel, u] = METRICS[metric];
  const expRows = (d?.rows || []).map((r) => ({ ...(d.mode === "days" ? { date: r.key } : { _period_label: r.label }), giorno: r.label, ...Object.fromEntries(HOURS.map((h) => [`h${h}`, r.cells[h] ?? null])) }));
  return (
    <PageState header={header} loading={loading} error={error} data={d} testid="ea-power-heatmap">
      {d && <>
        <div className="card-elevated p-4 flex flex-wrap items-end gap-4" data-testid="ea-heat-controls">
          <EaField label="Vista"><EaSeg value={mode} onChange={setMode} options={[["days", "Giorni × ore"], ["weekly", "Settimana × ore"]]} testid="ea-heat-mode" /></EaField>
          <EaField label="Mese">
            <select value={month} onChange={(e) => setFilter({ month: e.target.value })} className={inputCls} data-testid="ea-heat-month">
              {mode === "weekly" && <option value="">Tutto l'anno</option>}
              {MONTHS_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </EaField>
          <EaField label="Metrica"><EaSeg value={metric} onChange={setMetric} options={Object.entries(METRICS).map(([k, [l]]) => [k, l])} testid="ea-heat-metric" /></EaField>
          <EaField label={`Evidenzia sopra (${u})`}><input type="number" min="0" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="es. 1500" className={`${inputCls} w-32`} data-testid="ea-heat-threshold" /></EaField>
        </div>
        <EaChartCard title={`${mLabel} · ${d.mode === "days" ? `${MONTHS_FULL[(d.month || 1) - 1]} ${d.year}` : `media ${d.month ? MONTHS_FULL[d.month - 1] : "anno"} ${d.year}`}`}
          subtitle={metric === "cost" ? "Costo teorico PUN del profilo, non costo fatturato." : `Colori dal minimo al massimo del periodo · ${u}${d.mode === "days" ? " · * giorno incompleto" : ""}`} testid="ea-heat-card"
          right={<EaExportButton filename={`power_heatmap_${d.mode}_${d.year}${d.month ? `_${d.month}` : ""}`} commodity="POWER" year={d.year}
            period={d.month ? `${MONTHS_FULL[d.month - 1]} ${d.year}` : `Anno ${d.year}`} testid="ea-heat-export"
            columns={[{ key: d.mode === "days" ? "date" : "giorno", label: "Giorno" }, ...HOURS.map((h) => ({ key: `h${h}`, label: `${h.padStart(2, "0")}:00 (${u})` }))]} rows={expRows} />}>
          {d.rows.length ? <Grid d={d} threshold={threshold} onCell={(date) => navigate({ pathname: "/energy-analytics/power/profili", search: `${search ? `${search}&` : "?"}day=${date}` })} />
            : <EaNote testid="ea-heat-nodata">Dati quartorari non disponibili per il periodo.</EaNote>}
        </EaChartCard>
      </>}
    </PageState>
  );
}
